import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as AlgovexIcon } from '../assets/algovex-wordmark-dark.svg';
import { ReactComponent as AlgovexLockup } from '../assets/algovex-lockup-tagline-dark.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requestPasswordReset, getMembershipTiers, createCheckoutSession } from '../api';
import EmbeddedCheckoutModal from '../components/EmbeddedCheckoutModal';

const SIGNUP_PLANS = [
  {
    key: 'free',
    name: 'Free',
    price: '$0/mo',
    color: 'var(--muted)',
    highlights: ['5 saved strategies', '5 backtests/day', '3 AI builds/day', '5 analyst msgs/day'],
  },
  {
    key: 'basic',
    name: 'Basic',
    price: '$19/mo',
    color: 'var(--blue)',
    highlights: ['Unlimited strategies', 'Unlimited backtests', 'Unlimited AI', 'Unlimited analyst'],
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '$49/mo',
    color: 'var(--teal)',
    highlights: ['Everything in Basic', 'Priority support'],
  },
];

export default function AuthPage() {
  const { login, register, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.state?.mode || 'login'); // 'login' | 'signup' | 'forgot'
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [tierData, setTierData] = useState({});
  const [checkoutPriceId, setCheckoutPriceId] = useState(null);

  useEffect(() => {
    if (location.state?.mode) setMode(location.state.mode);
  }, [location.state]);

  // Where to land after auth. Router state (`from`) covers in-app redirects;
  // the `?next=` query param covers cold loads from external links (e.g. email
  // deep links), where router state doesn't survive. Only same-origin paths are
  // honored to avoid open-redirects.
  const nextParam = new URLSearchParams(location.search).get('next');
  const safeNext = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;
  const redirectTarget = location.state?.from || safeNext || '/';

  useEffect(() => {
    getMembershipTiers()
      .then((res) => {
        const map = {};
        for (const t of res.data) map[t.tier] = t;
        setTierData(map);
      })
      .catch(() => {});
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');  // general error string
  const [fieldErrors, setFieldErrors] = useState({}); // { email: [...], password: [...], ... }
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);
    try {
      if (mode === 'forgot') {
        await requestPasswordReset({ email });
        setResetSent(true);
        setLoading(false);
        return;
      }
      const redirectTo = redirectTarget;
      if (mode === 'login') {
        await login(email, password);
        navigate(redirectTo);
      } else {
        await register(email, password, confirmPassword);
        const priceId = selectedPlan !== 'free' && tierData[selectedPlan]?.stripe_monthly_price_id;
        if (priceId) {
          // Account was created either way; the checkout modal below handles success/close.
          setCheckoutPriceId(priceId);
          setLoading(false);
          return;
        }
        navigate(redirectTo);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data) {
        if (typeof data === 'string') {
          setError(data);
        } else if (data.detail) {
          setError(data.detail);
        } else {
          // DRF returns field-level errors as { field: ["msg", ...] }
          setFieldErrors(data);
        }
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCheckoutClientSecret = useCallback(async () => {
    const res = await createCheckoutSession({ price_id: checkoutPriceId });
    return res.data.client_secret;
  }, [checkoutPriceId]);

  const finishCheckoutAndRedirect = async () => {
    setCheckoutPriceId(null);
    await refreshUser();
    navigate(redirectTarget);
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100%', background: 'var(--bg)',
    }}>
      <Helmet>
        <title>{mode === 'signup' ? 'Sign Up' : mode === 'forgot' ? 'Reset Password' : 'Sign In'} — Algovex</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div style={{
        width: 380, background: 'var(--card)', borderRadius: 12,
        padding: 32, boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
        border: '1px solid var(--border)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <AlgovexLockup className="auth-lockup-logo" style={{ width: 260, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
            {mode === 'login' ? 'Sign in to your account' : mode === 'signup' ? 'Create your free account' : 'Reset your password'}
          </div>
        </div>

        {/* Plan picker — shown only during signup */}
        {mode === 'signup' && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Choose your plan</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {SIGNUP_PLANS.map((plan) => {
                const selected = selectedPlan === plan.key;
                return (
                  <button
                    key={plan.key}
                    type="button"
                    onClick={() => setSelectedPlan(plan.key)}
                    style={{
                      flex: 1,
                      padding: '10px 6px',
                      borderRadius: 8,
                      border: selected ? `2px solid ${plan.color}` : '1px solid var(--border)',
                      background: selected ? 'rgba(255,255,255,0.04)' : 'var(--surface)',
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, color: plan.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{plan.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{plan.price}</div>
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 0 0', display: 'block' }}
            >
              Compare plans →
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} key={mode}>
          <label style={labelStyle}>Email</label>
          <input
            name="email" type="email" autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            required style={inputStyle} autoFocus
          />
          <FieldError messages={fieldErrors.email} />

          {mode === 'forgot' && resetSent && (
            <div style={{ color: 'var(--teal)', fontSize: 13, marginTop: 12, lineHeight: 1.5 }}>
              If an account with that email exists, a reset link has been sent. Check your inbox.
            </div>
          )}

          {mode !== 'forgot' && (
            <>
              <label style={labelStyle}>Password</label>
              <input
                name="password" type="password"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={8} style={inputStyle}
              />
              <FieldError messages={fieldErrors.password} />
              {mode === 'signup' && !fieldErrors.password && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, lineHeight: 1.5 }}>
                  At least 8 characters. Can't be entirely numeric or a commonly used password.
                </div>
              )}
            </>
          )}

          {mode === 'login' && (
            <div style={{ textAlign: 'right', marginTop: 4 }}>
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); setFieldErrors({}); setResetSent(false); }}
                style={linkBtnStyle}
              >
                Forgot password?
              </button>
            </div>
          )}

          {mode === 'signup' && (
            <>
              <label style={labelStyle}>Confirm Password</label>
              <input
                name="confirm_password" type="password" autoComplete="new-password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                required style={inputStyle}
              />
              <FieldError messages={fieldErrors.confirm_password} />
            </>
          )}

          {error && (
            <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}

          <button type="submit" disabled={loading || (mode === 'forgot' && resetSent)} style={{
            marginTop: 16, width: '100%', padding: '10px 0',
            fontSize: 14, fontWeight: 700,
            background: loading ? 'var(--subtle)' : 'var(--teal)', color: 'var(--bg)',
            border: 'none', borderRadius: 8, cursor: loading ? 'not-allowed' : 'pointer',
          }}>
            {loading
              ? '…'
              : mode === 'login'
              ? 'Sign In'
              : mode === 'signup'
              ? (selectedPlan === 'free' ? 'Sign Up' : 'Sign Up & Continue to Checkout')
              : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: 'var(--muted)' }}>
          {mode === 'login' ? (
            <>Don't have an account?{' '}
              <button onClick={() => { setMode('signup'); setError(''); setFieldErrors({}); setPassword(''); setConfirmPassword(''); }}
                style={linkBtnStyle}>Sign up</button>
            </>
          ) : mode === 'signup' ? (
            <>Already have an account?{' '}
              <button onClick={() => { setMode('login'); setError(''); setFieldErrors({}); setPassword(''); setConfirmPassword(''); }}
                style={linkBtnStyle}>Sign in</button>
            </>
          ) : (
            <button onClick={() => { setMode('login'); setError(''); setFieldErrors({}); setResetSent(false); }}
              style={linkBtnStyle}>Back to sign in</button>
          )}
        </div>
      </div>

      {checkoutPriceId && (
        <EmbeddedCheckoutModal
          title="Complete your subscription"
          fetchClientSecret={fetchCheckoutClientSecret}
          onComplete={finishCheckoutAndRedirect}
          onClose={finishCheckoutAndRedirect}
        />
      )}
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', marginTop: 14, marginBottom: 4,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 14,
  border: '1px solid var(--border)', borderRadius: 6, boxSizing: 'border-box',
  background: 'var(--surface)', color: 'var(--text)',
};
const linkBtnStyle = {
  background: 'none', border: 'none', color: 'var(--teal)',
  fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13,
};

function FieldError({ messages }) {
  if (!messages || messages.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
      {[].concat(messages).map((msg, i) => <div key={i}>{msg}</div>)}
    </div>
  );
}
